import { BuySelectedAddon } from '@buy/state/add-on'
import { BuyPlan } from '@buy/state/plan'
import { BuyItems } from '@buy/state/checkout/state'
import { BillingDetailedSubscriptionPlan } from '@common/state/billing/state'
import { PRODUCT_SKU, BILLING_TYPES, BillingPeriod, BillingPeriodBillingTypesMapper } from '@common/constants'
import { ADDONS } from '@buy/constants'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { isEmpty } from 'lodash-es'
import { SubscriptionInfo } from './get-subscription-info'
import { getUsagePriceType, isNoneUsagePrice } from './get-usage-price-values'

export const getPaymentItems = (
    selectedPlan: BuyPlan,
    organizers: number,
    selectedBillingFrequency: string,
    selectedAddons: BuySelectedAddon[],
    subscriptionInfo: SubscriptionInfo,
    productFamily = 'GoToResolve' // TODO: WAE1-4451: pass in the actual product family
) => {
    const items: BuyItems[] = []
    const billingFrequency = selectedBillingFrequency === 'YEAR' ? BILLING_TYPES.ANNUAL : BILLING_TYPES.MONTHLY
    const contractLengthInMonth = billingFrequency === BILLING_TYPES.ANNUAL ? 12 : 1

    const planSubscription: BuyItems = {
        itemId: 0,
        productSku: selectedPlan.sKU,
        productFamily,
        paymentPlan: billingFrequency,
        quantity: organizers,
        contractLengthInMonth,
        subscriptionsToUpgrade: null,
    }

    if (subscriptionInfo.plan && subscriptionInfo.isReplacementBooking && !subscriptionInfo.isTrial) {
        if (!subscriptionInfo.canUpgradePlan) {
            planSubscription.productSku = subscriptionInfo.plan.productSku
            planSubscription.quantity = subscriptionInfo.plan.quantity
        }

        planSubscription.subscriptionsToUpgrade = [
            {
                subscriptionId: subscriptionInfo.plan.subscriptionId,
                quantity: subscriptionInfo.plan.quantity,
            },
        ]

        subscriptionInfo.addons.forEach((addon: BillingDetailedSubscriptionPlan) => {
            planSubscription.subscriptionsToUpgrade?.push({
                subscriptionId: addon.subscriptionId,
                quantity: addon.quantity,
            })
        })
    }

    if (subscriptionInfo.isReplacementBooking) items.push(planSubscription)

    // add addon products to the items object
    if (selectedAddons.length > 0) {
        selectedAddons.forEach((addon: BuySelectedAddon) => {
            const v2plan = items.find(
                (item) =>
                    (item.productSku === PRODUCT_SKU.GTR_REMOTE_SUPPORT_NAMED_V2 ||
                        item.productSku === PRODUCT_SKU.GTR_REMOTE_ACCESS) &&
                    !item.subscriptionsToUpgrade
            )
            if (v2plan && addon.sKU.includes(ADDONS.AGENT)) {
                v2plan.quantity += addon.quantity
            } else {
                items.push({
                    itemId: items.length,
                    productSku: addon.sKU,
                    productFamily,
                    paymentPlan: billingFrequency,
                    quantity: addon.quantity,
                    contractLengthInMonth,
                    subscriptionsToUpgrade: null,
                })
            }
        })
    }

    return items
}

export const getCartItems = (
    baseLine: OrderSummaryLine,
    additionalLines: OrderSummaryLine[],
    addonLines: OrderSummaryLine[],
    subscriptionInfo: SubscriptionInfo,
    selectedBillingFrequency: BillingPeriod,
    productFamily: string
) => {
    const contractLengthInMonth =
        BillingPeriodBillingTypesMapper[selectedBillingFrequency] === BILLING_TYPES.ANNUAL ? 12 : 1
    const items: BuyItems[] = []
    const planSubscription: BuyItems = {
        itemId: 0,
        productSku: baseLine.key,
        productFamily,
        paymentPlan: BillingPeriodBillingTypesMapper[selectedBillingFrequency],
        quantity: !isEmpty(baseLine.quantityMultiplier)
            ? Number(baseLine.quantityMultiplier) * baseLine.multiplier
            : baseLine.multiplier,
        contractLengthInMonth,
        subscriptionsToUpgrade: null,
    }

    if (subscriptionInfo.plan && subscriptionInfo.isReplacementBooking && !subscriptionInfo.isTrial) {
        if (!subscriptionInfo.canUpgradePlan) {
            planSubscription.productSku = subscriptionInfo.plan.productSku
            planSubscription.quantity = subscriptionInfo.plan.quantity
        }

        planSubscription.subscriptionsToUpgrade = [
            {
                subscriptionId: subscriptionInfo.plan.subscriptionId,
                quantity: subscriptionInfo.plan.quantity,
            },
        ]

        subscriptionInfo.addons.forEach((addon: BillingDetailedSubscriptionPlan) => {
            planSubscription.subscriptionsToUpgrade?.push({
                subscriptionId: addon.subscriptionId,
                quantity: addon.quantity,
            })
        })
    }

    if (subscriptionInfo.isReplacementBooking) items.push(planSubscription)

    const updatedAdditionalLines = additionalLines.map((item) => {
        const newItem = { ...item }
        newItem.quantity = newItem.multiplier
        return newItem
    })
    const otherLines = [...updatedAdditionalLines, ...addonLines]
    const otherItems: BuyItems[] = otherLines.map((item, index) => {
        const usagePriceType = getUsagePriceType(item.allPrices, item.usagePrice)
        // let calculatedQuantity = !isEmpty(item.quantityMultiplier) ? Number(item.quantityMultiplier) * item.quantity : item.quantity
        return {
            itemId: index + 1,
            productSku: item.key,
            productFamily,
            paymentPlan: isNoneUsagePrice(usagePriceType)
                ? BillingPeriodBillingTypesMapper[selectedBillingFrequency]
                : BILLING_TYPES.MONTHLY,
            quantity: !isEmpty(item.quantityMultiplier)
                ? Number(item.quantityMultiplier) * item.quantity
                : item.quantity,
            contractLengthInMonth: isNoneUsagePrice(usagePriceType) ? contractLengthInMonth : 1,
            subscriptionsToUpgrade: null,
        }
    })
    return [...items, ...otherItems]
}
